import React, { useState } from "react";
import { useQuote } from "../context/QuoteContext";
import api from "../api";
import { formatEur, parseRange, formatDateTimeIt, parseDurationDiscounts, freeRitiroPeriods } from "../utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, TrendingDown, Search, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function SelfStorageComparison() {
  const { settings, monthlyTotal, ritiro, ritiroTotal, totalVolume, marketData, setMarketData, marketDataAt, setMarketDataAt, effectiveGaugePeriods } = useQuote();
  const [loading, setLoading] = useState(false);
  const [occupazione, setOccupazione] = useState(25);
  if (!settings) return null;

  const ssVolume = occupazione > 0 ? totalVolume / (occupazione / 100) : totalVolume;
  const SS_HEIGHT = 2.5; // altezza standard box self-storage (m)
  const ssArea = ssVolume / SS_HEIGHT;

  const [canoneMin, canoneMax] = parseRange(settings.ssCanone);
  const [cauzMin, cauzMax] = parseRange(settings.ssCauzione);

  // media dai self-storage trovati (quando disponibili)
  const hasMarket = marketData.length > 0;
  const nums = (key, allowZero = false) => marketData.map((c) => Number(c[key])).filter((v) => !isNaN(v) && (allowZero ? v >= 0 : v > 0));
  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
  const avgMonthly = avg(nums("monthlyPrice"));
  const avgDeposit = avg(nums("deposit", true));

  // valori self-storage: media di mercato se disponibile, altrimenti media del range impostato
  const [ssTrasMin, ssTrasMax] = parseRange(settings.ssTrasporto);
  const ssMonthly = hasMarket ? avgMonthly : (canoneMin + canoneMax) / 2;
  const ssDeposit = hasMarket ? avgDeposit : (cauzMin + cauzMax) / 2;
  const ssTransport = (ssTrasMin + ssTrasMax) / 2; // costo trasporto self-storage (una tantum)

  // periodi e sconti durata StorMi
  const periods = effectiveGaugePeriods.map((p) => p.months);
  const durDiscs = parseDurationDiscounts(settings.durationDiscounts) || [];
  const discountPct = (m) => {
    const d = durDiscs.find((x) => Number(x.months) === Number(m));
    return d ? Number(d.percent) || 0 : 0;
  };
  const freeSet = freeRitiroPeriods(ritiro);
  const isFree = (m) => freeSet.includes(Number(m));

  // costo MENSILE ricorrente (solo canone) per periodo — una tantum mostrate a parte
  const rows = periods.map((m) => {
    const storMonthly = monthlyTotal * (1 - discountPct(m) / 100);
    return { m, storMonthly, ssMonthly, save: ssMonthly - storMonthly, free: isFree(m) };
  });

  // costi una tantum
  const storTransportOnce = ritiro && ritiro.enabled ? ritiroTotal : 0;
  const freePeriodsList = freeSet;

  const research = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/market-research", {
        cap: ritiro.cap || "", citta: ritiro.citta || "", volumeM3: ssVolume,
      });
      setMarketData(data.competitors || []);
      setMarketDataAt(new Date().toISOString());
      toast.success("Dati di mercato aggiornati");
    } catch {
      toast.error("Impossibile caricare i dati di mercato");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="self-storage-comparison">
      <h3 className="font-display font-semibold text-lg mb-4">Self-storage tradizionale vs StorMi</h3>

      <div className="text-xs text-slate-500 mb-3">
        Confronto del <strong>canone mensile</strong> per durata. Self-storage: {hasMarket ? "media di mercato" : "stima da impostazioni"} (canone €{formatEur(ssMonthly)}/mese). StorMi: canone mensile scontato in base alla durata. Cauzione e trasporto sono <strong>costi una tantum</strong>, mostrati a parte sotto la tabella.
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left p-3 font-medium">Canone mensile</th>
              {rows.map((r) => (
                <th key={r.m} className="text-right p-3 font-medium" data-testid={`ss-period-head-${r.m}`}>{r.m} {r.m === 1 ? "mese" : "mesi"}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr>
              <td className="p-3 text-slate-600">Self-storage</td>
              {rows.map((r) => (
                <td key={r.m} className="p-3 text-right text-slate-500" data-testid={`ss-monthly-${r.m}`}>€{formatEur(r.ssMonthly)}<span className="text-slate-400 text-xs">/mese</span></td>
              ))}
            </tr>
            <tr>
              <td className="p-3 text-[#1a5f5a] font-medium">StorMi</td>
              {rows.map((r) => (
                <td key={r.m} className="p-3 text-right font-semibold text-[#1a5f5a]" data-testid={`stormi-monthly-${r.m}`}>€{formatEur(r.storMonthly)}<span className="text-[#1a5f5a]/60 text-xs">/mese</span></td>
              ))}
            </tr>
            <tr className="bg-green-50">
              <td className="p-3 text-green-700 font-medium flex items-center gap-1.5"><TrendingDown className="h-4 w-4" /> Risparmio mensile</td>
              {rows.map((r) => (
                <td key={r.m} className={`p-3 text-right font-semibold ${r.save < 0 ? "text-red-600" : "text-green-700"}`} data-testid={`ss-save-${r.m}`}>
                  {r.save < 0 ? "-" : ""}€{formatEur(Math.abs(r.save))}<span className={`text-xs ${r.save < 0 ? "text-red-600/60" : "text-green-700/60"}`}>/mese</span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-md border border-slate-200 p-3" data-testid="ss-una-tantum">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Costi una tantum — Self-storage</div>
          <div className="flex items-center justify-between text-sm text-slate-600"><span>Cauzione</span><span data-testid="ss-deposit-once">€{formatEur(ssDeposit)}</span></div>
          <div className="flex items-center justify-between text-sm text-slate-600 mt-1"><span>Trasporto</span><span data-testid="ss-transport-once">€{formatEur(ssTransport)}</span></div>
        </div>
        <div className="rounded-md border border-slate-200 p-3" data-testid="stormi-una-tantum">
          <div className="text-xs font-medium text-[#1a5f5a] uppercase tracking-wide mb-2">Costi una tantum — StorMi</div>
          <div className="flex items-center justify-between text-sm text-slate-600"><span>Trasporto (ritiro)</span><span data-testid="stormi-transport-once">€{formatEur(storTransportOnce)}</span></div>
          {freePeriodsList.length ? (
            <div className="text-[11px] text-green-600 font-medium mt-1" data-testid="stormi-free-note">🆓 Trasporto gratis per la durata di {freePeriodsList.slice().sort((a, b) => a - b).join(", ")} {freePeriodsList.length === 1 && freePeriodsList[0] === 1 ? "mese" : "mesi"}</div>
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={research} disabled={loading} className="gap-1.5" data-testid="market-research-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Aggiorna con dati di mercato attuali
            </Button>
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-slate-500">Occupazione</label>
              <div className="relative">
                <Input type="number" min={10} max={100} value={occupazione}
                  onChange={(e) => setOccupazione(Math.min(100, Math.max(1, parseFloat(e.target.value) || 0)))}
                  className="w-20 h-9 pr-6" data-testid="occupazione-input" />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
              </div>
            </div>
          </div>
          <span className="text-[11px] text-slate-400">
            Box self-storage ~{ssArea.toFixed(2)} m² (oggetti {totalVolume.toFixed(2)} m³ al {occupazione}%){ritiro.citta ? ` · ${ritiro.citta}` : ""}
          </span>
        </div>
        {marketData.length > 0 && (
          <div className="mt-3 overflow-hidden rounded-md border border-slate-200">
            {marketDataAt && (
              <div className="flex items-center gap-1.5 text-[11px] text-slate-400 px-2.5 py-1.5 bg-slate-50 border-b border-slate-100" data-testid="market-analyzed-at">
                <Clock className="h-3 w-3" /> Analisi del {formatDateTimeIt(marketDataAt)}
              </div>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <th className="text-left p-2.5 font-medium">Self-storage (esempio)</th>
                  <th className="text-right p-2.5 font-medium">€/mese</th>
                  <th className="text-right p-2.5 font-medium">Cauzione</th>
                  <th className="text-right p-2.5 font-medium">Dist.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {marketData.map((c, i) => (
                  <tr key={i} data-testid={`competitor-${i}`}>
                    <td className="p-2.5 text-slate-700">
                      {c.name}
                      <div className="text-[11px] text-slate-400">{c.notes}</div>
                      {c.sourceUrl ? (
                        <a href={c.sourceUrl} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-[#1a5f5a] hover:underline mt-0.5"
                          data-testid={`competitor-source-${i}`}>
                          <ExternalLink className="h-3 w-3" /> Fonte prezzo
                        </a>
                      ) : (
                        <div className="text-[11px] text-slate-300" data-testid={`competitor-source-${i}`}>fonte non disponibile</div>
                      )}
                    </td>
                    <td className="p-2.5 text-right font-medium">€{formatEur(c.monthlyPrice)}<span className="text-slate-400 text-xs">/mese</span></td>
                    <td className="p-2.5 text-right text-slate-500">€{formatEur(c.deposit)}</td>
                    <td className="p-2.5 text-right text-slate-500">{c.distance != null ? `${c.distance} km` : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
